import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Button, NavBar, Skeleton, Toast, Dialog} from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import EmptyState from "../../../components/EmptyState";
import {t} from "../../../i18n";
import {useTmsTaskWaypoint, useTmsTaskOperations, useTransportStore} from "../store/transportStore";
import {TmsWaypointWithAddressType} from "../graphql/queries/getTmsPublishedTaskByDriverAccountId";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {formatTimestampByLanguage} from "../../../utils/dateTimeUtils";
import {useTranslation} from "react-i18next";
import TransportInfoItem from "../components/TransportInfoItem";
import TransportPhotoUploadCard from "../components/TransportPhotoUploadCard";
import TransportPhotoViewer from "../components/TransportPhotoViewer";
import transportPhotoService, {TransportPhotoType, TransportPositionType, uploadTransportPhotosWithTimeout} from "../services/TransportPhotoService";
import {TmsPath, TmsPathPre} from "../../../enums/TmsNavigatePathEnum";
import { TmsOperationSourceEnum } from "../../../enums/TmsEnum";

interface TransportArrivingPageProps {}

const TransportArrivingPage: React.FC<TransportArrivingPageProps> = () => {
    const navigate = useNavigate();
    const { taskWayPointId } = useParams<{
        taskWayPointId: string;
    }>();
    const { i18n } = useTranslation();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [waypointData, setWaypointData] = useState<TmsWaypointWithAddressType>();
    const { tmsTaskWaypoint, loading, refreshTmsTaskWaypointList } = useTmsTaskWaypoint(taskWayPointId || '');
    const tmsTaskWaypointList = useTransportStore(state => state.tmsTaskWaypointList);
    const { updateTmsTaskWaypointToArrived } = useTmsTaskOperations();
    const [photos, setPhotos] = useState<Record<TransportPositionType, TransportPhotoType | undefined>>({} as Record<TransportPositionType, TransportPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);

    // 获取下一个未完成的节点
    const nextWaypoint = React.useMemo(() => {
        if (!waypointData || !tmsTaskWaypointList || tmsTaskWaypointList.length === 0) {
            return null;
        }
        // 按 sequence 排序找到当前节点
        const sortedWaypoints = [...tmsTaskWaypointList].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
        const currentIndex = sortedWaypoints.findIndex(
            wp => wp.taskWayPointId === waypointData.taskWayPointId
        );
        // 从当前节点后面找到第一个未完成的节点
        if (currentIndex >= 0 && currentIndex < sortedWaypoints.length - 1) {
            for (let i = currentIndex + 1; i < sortedWaypoints.length; i++) {
                const wp = sortedWaypoints[i];
                if (wp.status !== 'DONE') {
                    return wp;
                }
            }
        }
        return null;
    }, [waypointData, tmsTaskWaypointList]);

    useEffect(() => {
        if (tmsTaskWaypoint) {
            setWaypointData(tmsTaskWaypoint);
        }
    }, [tmsTaskWaypoint]);

    // 拍照或从相册选择
    const takePhoto = async (type: TransportPositionType, file: File) => {
        try {
            setIsLoading(true);
            await transportPhotoService.handlePhotoNotUpload(
                type,
                file,
                (updateFn: (prev: Record<TransportPositionType, TransportPhotoType | undefined>) => Record<TransportPositionType, TransportPhotoType | undefined>) => {
                    setPhotos((prev) => updateFn(prev));
                }
            );
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: TransportPositionType) => {
        Dialog.confirm({
            content: t('pages.tms.transporting.arriving.deleteContent'),
            confirmText: t('pages.tms.transporting.arriving.deleteConfirm'),
            cancelText: t('pages.tms.transporting.arriving.deleteCancel'),
            onConfirm: () => {
                transportPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    // 检查照片数量：至少需要两个照片
    const hasEnoughPhotos = () => {
        const photoCount = [
            photos.signFor?.url,
            photos.warehousePhoto?.url,
            photos.doorNumber?.url
        ].filter(Boolean).length;
        return photoCount >= 2;
    };

    // 标记到达
    const handleMarkArrived = async () => {
        if (!waypointData || !taskWayPointId) {
            Toast.show({
                content: t('pages.tms.transporting.noWaypointInfo'),
                position: 'bottom',
            });
            return;
        }

        if (!hasEnoughPhotos()) {
            Toast.show({
                content: t('pages.tms.transporting.arriving.photoRequired'),
                position: 'bottom',
            });
            return;
        }

        setIsLoading(true);
        try {
            const currentPhotos = {...photos};
            // 获取所有存在的照片的 objectKey
            const existingPhotoKeys = [
                photos.signFor?.objectKey,
                photos.warehousePhoto?.objectKey,
                photos.doorNumber?.objectKey
            ].filter((key): key is string => Boolean(key));
            //仅执行乐观提交逻辑，不进行网络请求
            await updateTmsTaskWaypointToArrived({
                tmsTaskWayPointId: parseInt(taskWayPointId),
                taskId: waypointData.taskId || 0,
                photosS3KeysJson: existingPhotoKeys,
            }, true); // 乐观提交不传 operationSource
            setPhotos(currentPhotos);

            // 图片上传，带超时控制
            const photoKeys = await uploadTransportPhotosWithTimeout(
                Object.values(photos)
                    .filter((item): item is TransportPhotoType => item?.objectKey !== undefined && item?.url !== undefined)
            );

            //真正的调用接口
            const success = await updateTmsTaskWaypointToArrived({
                tmsTaskWayPointId: parseInt(taskWayPointId),
                taskId: waypointData.taskId || 0,
                photosS3KeysJson: photoKeys,
                operationSource: TmsOperationSourceEnum.APP,
            }, false);

            if (success) {
                Toast.show({
                    content: t('pages.tms.transporting.arrivedSuccess'),
                    position: 'bottom',
                });
                // 刷新列表数据
                await refreshTmsTaskWaypointList();

                // 如果是终点（waypointType=3），返回列表页面
                if (waypointData.waypointType === 3) {
                    navigate(TmsPath.TMS_TRANSPORTING);
                } else {
                    // 返回上一页
                    navigate(-1);
                }
            } else {
                Toast.show({
                    content: t('pages.tms.transporting.arrivedFailed'),
                    position: 'bottom',
                });
            }
        } catch (error: any) {
            console.error('Failed to mark arrived:', error);
            if (error.networkError) {
                // 网络错误处理
                Toast.show({
                    content: t('pages.tms.transporting.arrivedFailed'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.tms.transporting.arrivedFailed'),
                    position: 'bottom',
                });
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 获取站点编号显示文本
    const getSiteCodeText = (): string => {
        const isStartPoint = waypointData?.waypointType === 1;
        const isEndPoint = waypointData?.waypointType === 3;
        if (isStartPoint) {
            return t('pages.tms.start');
        }
        if (isEndPoint) {
            return t('pages.tms.end');
        }
        return waypointData?.sequence?.toString() || '-';
    };

    if (loading) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.tms.transporting.arriving.title')}
                    </NavBar>
                </div>
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={5} animated />
                <Skeleton.Title animated />
                <Skeleton.Paragraph lineCount={3} animated />
            </div>
        );
    }

    if (!waypointData) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.tms.transporting.arriving.title')}
                    </NavBar>
                </div>
                <EmptyState description={t('pages.tms.noTransportInfo')}/>
            </div>
        );
    }

    const address = buildAddressFromAppointment({
        addressLine1: waypointData.addressLine1 ?? undefined,
        addressLine2: waypointData.addressLine2 ?? undefined,
        city: waypointData.city ?? undefined,
        state: waypointData.state ?? undefined,
        zipcode: waypointData.zipcode ?? undefined,
        country: waypointData.country ?? undefined
    });

    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>
                    {waypointData.wayPointName || t('pages.tms.transporting.arriving.title')}
                </NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* 途经点信息 */}
                <div className="mb-1 bg-white rounded-lg p-4 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.tms.transporting.arriving.information')}</h2>
                    <div className="mb-1">
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.name')} value={waypointData.wayPointName || '-'} />
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.sequence')} value={getSiteCodeText()} />
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.contact')} value={waypointData.contactName || '-'} />
                        <TransportInfoItem 
                            label={t('pages.tms.transporting.arriving.phone')} 
                            value={`${waypointData.contactPhone || ''}${waypointData.ext ? `-${waypointData.ext}` : ''}`} 
                        />
                        <TransportInfoItem label={t('pages.tms.transporting.arriving.address')} value={address} />
                        {waypointData.planArrivalTime && (
                            <TransportInfoItem
                                label={t('pages.tms.transporting.planArrivalTime')}
                                value={formatTimestampByLanguage(waypointData.planArrivalTime, i18n.language) + (waypointData.planArrivalTimezone ? ` (${waypointData.planArrivalTimezone})` : '')}
                            />
                        )}
                        {waypointData.planDepartureTime && (
                            <TransportInfoItem
                                label={t('pages.tms.transporting.planDepartureTime')}
                                value={formatTimestampByLanguage(waypointData.planDepartureTime, i18n.language) + (waypointData.planDepartureTimezone ? ` (${waypointData.planDepartureTimezone})` : '')}
                            />
                        )}
                    </div>
                </div>

                {/*上传图片*/}
                <div className="mb-2 bg-white rounded-lg p-4 shadow-sm">
                <h2 className="text-base font-semibold mb-1">{t('pages.tms.transporting.arriving.photos')}</h2>
                <p className="text-sm text-gray-600 mb-1">
                    <span>{t('pages.tms.transporting.arriving.photoDesc')}</span>
                    <span className={`text-green-600 text-base`}>{t('pages.tms.transporting.arriving.photoDesc1')}</span>
                    {t('pages.tms.transporting.arriving.punctuation')}
                </p>
                <div className="grid grid-cols-3 gap-2 my-4">
                    <TransportPhotoUploadCard
                        photoUrl={photos.warehousePhoto?.url}
                        type="warehousePhoto"
                        description={``}
                        onTakePhoto={takePhoto}
                        onDelete={deletePhoto}
                        onView={handleViewPhoto}
                    />
                    <TransportPhotoUploadCard
                        photoUrl={photos.doorNumber?.url}
                        type="doorNumber"
                        description={``}
                        onTakePhoto={takePhoto}
                        onDelete={deletePhoto}
                        onView={handleViewPhoto}
                    />
                    <TransportPhotoUploadCard
                        photoUrl={photos.signFor?.url}
                        type="signFor"
                        description={``}
                        onTakePhoto={takePhoto}
                        onDelete={deletePhoto}
                        onView={handleViewPhoto}
                    />

                </div>
                <TransportPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
                </div>
            </div>

            {/* 提交按钮 */}
            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <Button
                    block
                    color="primary"
                    loading={isLoading}
                    disabled={!hasEnoughPhotos() || isLoading }
                    onClick={handleMarkArrived}
                >
                    {t('pages.tms.transporting.arrivedFinished')}
                </Button>
            </div>
        </div>
    );
};

export default TransportArrivingPage;
